import { useState, useRef, useCallback } from "react";
import { useAuthStore } from "../store/authStore";
import { api } from "../lib/api";
import { useT } from "../i18n";

const DEFAULT_COLOR = "#00843D";

function ColorSwatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: 8, border: selected ? "3px solid var(--clr-text)" : "2px solid transparent",
        background: color, cursor: "pointer", transition: "transform 0.1s, border 0.1s",
        outline: "none", flexShrink: 0,
        transform: selected ? "scale(1.15)" : "scale(1)",
      }}
      title={color}
    />
  );
}

const PRESET_COLORS = [
  "#00843D", "#1d4ed8", "#7c3aed", "#db2777", "#ea580c",
  "#0891b2", "#059669", "#4f46e5", "#dc2626", "#92400e",
];

export default function BrandingPage() {
  const t = useT();
  const user = useAuthStore(s => s.user);
  const updateBranding = useAuthStore(s => s.updateBranding);

  const [color, setColor] = useState(user?.primary_color || DEFAULT_COLOR);
  const [logoPreview, setLogoPreview] = useState<string | null>(user?.logo_url || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const orgId = user?.organization_id;

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) { setError(t("branding.errImageOnly")); return; }
    if (file.size > 2 * 1024 * 1024) { setError(t("branding.errTooBig")); return; }
    setError("");
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }, [t]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const saveColor = async () => {
    if (!orgId) return;
    setSaving(true); setError(""); setSuccess(false);
    try {
      await api.put(`/organizations/${orgId}/branding`, { primary_color: color });
      updateBranding(color, user?.logo_url ?? null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const uploadLogo = async () => {
    if (!orgId || !logoFile) return;
    setUploading(true); setError(""); setSuccess(false);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(`/api/organizations/${orgId}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || t("branding.errUploadFailed")); }
      const data = await res.json();
      updateBranding(user?.primary_color ?? null, data.logo_url);
      setLogoPreview(data.logo_url);
      setLogoFile(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) { setError(e.message); }
    finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!orgId) return;
    setSaving(true); setError("");
    try {
      await api.delete(`/organizations/${orgId}/logo`);
      updateBranding(user?.primary_color ?? null, null);
      setLogoPreview(null);
      setLogoFile(null);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const card: React.CSSProperties = {
    background: "var(--clr-surface)", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
    border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.5rem", boxShadow: "var(--clr-shadow-sm)",
  };

  const btn = (variant: "primary" | "ghost" | "danger" = "primary", disabled = false): React.CSSProperties => ({
    padding: "0.55rem 1.25rem", borderRadius: 999,
    border: variant === "ghost" ? "1px solid var(--clr-border)" : variant === "danger" ? "1px solid var(--clr-border)" : 0,
    background: variant === "primary" ? color : "var(--clr-surface)",
    color: variant === "primary" ? "#fff" : variant === "danger" ? "#ef4444" : "var(--clr-text3)",
    fontWeight: variant === "primary" ? 600 : 500, fontSize: "0.85rem", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1, transition: "opacity 0.15s",
  });

  return (
    <div className="page" style={{ maxWidth: 720, fontVariantNumeric: "tabular-nums" }}>
      <div style={{ marginBottom: "1.75rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("branding.title")}</h1>
        <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
          {t("branding.subtitle")}
        </p>
      </div>

      {error && (
        <div style={{ background: "#ef444412", border: "1px solid #ef444430", borderRadius: 12, padding: "0.75rem 1rem", color: "#ef4444", fontSize: "0.85rem", marginBottom: "1.25rem" }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 12, padding: "0.75rem 1rem", fontSize: "0.85rem", marginBottom: "1.25rem", boxShadow: "var(--clr-shadow-sm)" }}>
          <span style={{ width: 7, height: 7, borderRadius: 3.5, background: "#22c55e", display: "inline-block", flexShrink: 0 }} />
          <span style={{ color: "var(--clr-text2)", fontWeight: 500 }}>{t("branding.saved")}</span>
        </div>
      )}

      {/* ── Logo ── */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        <h2 style={{ margin: "0 0 1rem", fontSize: "1rem", fontWeight: 600 }}>{t("branding.logo")}</h2>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.83rem", color: "var(--clr-text3)", lineHeight: 1.6 }}>
          {t("branding.logoHelp")}
        </p>

        {/* Preview + drop zone */}
        <div style={{ display: "flex", gap: "1.25rem", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div
            onDrop={onDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileRef.current?.click()}
            style={{
              width: 200, height: 100, borderRadius: 12, border: `2px dashed ${dragOver ? color : "var(--clr-border)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "border-color 0.2s, background 0.2s",
              background: dragOver ? `${color}08` : "var(--clr-surface2)",
              overflow: "hidden", flexShrink: 0,
            }}
          >
            {logoPreview ? (
              <img src={logoPreview} alt={t("branding.logoPreviewAlt")} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: 12 }} />
            ) : (
              <div style={{ textAlign: "center", color: "var(--clr-text4)", fontSize: "0.78rem", padding: "0.5rem" }}>
                <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={1.6}
                     strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 6, opacity: 0.6 }}>
                  <path d="M12 16V4M7 9l5-5 5 5" />
                  <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
                </svg>
                <div>{t("branding.dropLine1")}<br />{t("branding.dropLine2")}</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {logoFile && (
              <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                {logoFile.name} · {(logoFile.size / 1024).toFixed(0)} KB
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={btn("ghost")} onClick={() => fileRef.current?.click()}>
                {logoPreview ? t("branding.changeLogo") : t("branding.selectLogo")}
              </button>
              {logoFile && (
                <button style={btn("primary", uploading)} onClick={uploadLogo} disabled={uploading}>
                  {uploading ? t("branding.uploading") : t("branding.saveLogo")}
                </button>
              )}
              {logoPreview && !logoFile && (
                <button style={btn("danger")} onClick={removeLogo} disabled={saving}>
                  {t("branding.removeLogo")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Color ── */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        <h2 style={{ margin: "0 0 0.25rem", fontSize: "1rem", fontWeight: 600 }}>{t("branding.primaryColor")}</h2>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.83rem", color: "var(--clr-text3)" }}>
          {t("branding.primaryColorHelp")}
        </p>

        {/* Presets */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1.25rem" }}>
          {PRESET_COLORS.map(c => (
            <ColorSwatch key={c} color={c} selected={color === c} onClick={() => setColor(c)} />
          ))}
        </div>

        {/* Custom picker */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: "0.83rem", color: "var(--clr-text2)", whiteSpace: "nowrap" }}>{t("branding.customColor")}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 44, height: 36, borderRadius: 10, border: "1px solid var(--clr-border)", cursor: "pointer", padding: 2 }}
            />
            <input
              type="text"
              value={color}
              onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setColor(v); }}
              style={{ width: 96, padding: "0.45rem 0.75rem", borderRadius: 12, border: "1px solid var(--clr-border)", background: "var(--clr-input-bg)", color: "var(--clr-text)", fontSize: "0.85rem", fontFamily: "monospace", outline: "none" }}
              placeholder="#000000"
            />
          </div>
        </div>

        {/* Live preview */}
        <div style={{ marginTop: "1.25rem", padding: "1rem", background: "var(--clr-surface2)", borderRadius: 12 }}>
          <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>{t("branding.preview")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button style={{ padding: "0.5rem 1.25rem", borderRadius: 999, border: 0, background: color, color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "default" }}>
              {t("branding.primaryButton")}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0.5rem 0.875rem", borderRadius: 999, background: `${color}18` }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: color, display: "inline-block" }} />
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color }}>{t("branding.activeElement")}</span>
            </div>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: color }} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.25rem" }}>
          <button style={btn("primary", saving || !/^#[0-9A-Fa-f]{6}$/.test(color))} onClick={saveColor} disabled={saving || !/^#[0-9A-Fa-f]{6}$/.test(color)}>
            {saving ? t("branding.saving") : t("branding.saveColor")}
          </button>
        </div>
      </div>

      {/* ── Reset ── */}
      <div style={{ ...card }}>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>{t("branding.resetTitle")}</h2>
        <p style={{ margin: "0 0 1rem", fontSize: "0.83rem", color: "var(--clr-text3)" }}>
          {t("branding.resetHelp")}
        </p>
        <button
          style={btn("ghost")}
          onClick={async () => {
            if (!orgId) return;
            setSaving(true);
            try {
              await api.put(`/organizations/${orgId}/branding`, { primary_color: DEFAULT_COLOR });
              if (logoPreview) await api.delete(`/organizations/${orgId}/logo`);
              updateBranding(DEFAULT_COLOR, null);
              setColor(DEFAULT_COLOR);
              setLogoPreview(null);
              setLogoFile(null);
            } catch (e: any) { setError(e.message); }
            finally { setSaving(false); }
          }}
          disabled={saving}
        >
          {t("branding.resetButton")}
        </button>
      </div>
    </div>
  );
}
